import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PROVINCES, money, transitCopy } from "@/lib/format";
import type { Address, Parcel } from "@/lib/types";
import { Alert, Badge, Button, Card, Field, Select, Spinner, Table, cx } from "./ui";

interface Problem {
  field: string;
  message: string;
  severity: "error" | "warning";
}

interface TestedRate {
  service: string;
  service_level: string;
  carrier_code?: string;
  cost_cents: number;
  price_cents: number;
  vat_cents: number;
  margin_cents: number;
  margin_percent: number;
  transit_days: number;
  collection_cutoff?: string;
  estimated_delivery?: string;
}

interface RateTestResult {
  ok: boolean;
  stage: "validation" | "credentials" | "carrier" | "quoted";
  detail: string;
  validation: Problem[];
  rates?: TestedRate[];
  elapsed_ms?: number;
  account_id?: number;
  base_url?: string;
  rate_card?: string;
}

const EMPTY: Address = {
  type: "business",
  contact_name: "",
  phone: "",
  email: "",
  street: "",
  suburb: "",
  city: "",
  province: "Gauteng",
  postal_code: "",
};

// A real Pretoria to Johannesburg parcel, so the form is useful the moment it
// opens rather than after five minutes of typing.
const SAMPLE: { collection: Address; delivery: Address } = {
  collection: {
    ...EMPTY,
    type: "business",
    contact_name: "Ashley",
    phone: "0821234567",
    street: "194 Bancor Avenue",
    suburb: "Menlyn",
    city: "Pretoria",
    province: "Gauteng",
    postal_code: "0181",
  },
  delivery: {
    ...EMPTY,
    type: "residential",
    contact_name: "Sam",
    phone: "0839876543",
    street: "5 Rivonia Road",
    suburb: "Sandton",
    city: "Johannesburg",
    province: "Gauteng",
    postal_code: "2196",
  },
};

/**
 * Quote a real route against the live carrier.
 *
 * Nothing is booked and nothing is saved. The value is seeing both numbers side
 * by side: what ShipLogic charges and what the customer would pay, which is the
 * fastest way to answer "why is this route expensive" without exporting
 * anything to a spreadsheet.
 */
export function RateTester() {
  const [collection, setCollection] = useState<Address>(SAMPLE.collection);
  const [delivery, setDelivery] = useState<Address>(SAMPLE.delivery);
  const [parcel, setParcel] = useState<Parcel>({
    length_mm: 300,
    width_mm: 200,
    height_mm: 150,
    weight_g: 2000,
  });
  const [declared, setDeclared] = useState("");

  const test = useMutation({
    mutationFn: () =>
      api.post<RateTestResult>("/v1/admin/settings/rate-test", {
        collection,
        delivery,
        parcels: [parcel],
        declared_value_cents: declared ? Math.round(Number(declared) * 100) : 0,
      }),
  });

  const result = test.data;
  const problemFor = (field: string) => result?.validation?.find((p) => p.field === field);
  const errorFor = (field: string) => {
    const problem = problemFor(field);
    return problem?.severity === "error" ? problem.message : undefined;
  };
  const warnings = (result?.validation ?? []).filter((p) => p.severity === "warning");

  return (
    <Card className="mb-6">
      <h2 className="text-lg font-bold tracking-tight">Live rate test</h2>
      <p className="mb-4 mt-1 text-sm text-ink-500">
        Quotes a real route against ShipLogic with the credentials above. Nothing is booked, nothing
        is billed, and no shipment is created.
      </p>

      <div className="grid gap-6 lg:grid-cols-2">
        <AddressFields
          title="Collect from"
          prefix="collection"
          value={collection}
          onChange={setCollection}
          errorFor={errorFor}
          warningFor={(field) => {
            const problem = problemFor(field);
            return problem?.severity === "warning" ? problem.message : undefined;
          }}
        />
        <AddressFields
          title="Deliver to"
          prefix="delivery"
          value={delivery}
          onChange={setDelivery}
          errorFor={errorFor}
          warningFor={(field) => {
            const problem = problemFor(field);
            return problem?.severity === "warning" ? problem.message : undefined;
          }}
        />
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-5">
        <Field
          label="Length (mm)"
          type="number"
          value={parcel.length_mm}
          onChange={(e) => setParcel({ ...parcel, length_mm: Number(e.target.value) })}
          error={errorFor("parcels.0.length_mm")}
        />
        <Field
          label="Width (mm)"
          type="number"
          value={parcel.width_mm}
          onChange={(e) => setParcel({ ...parcel, width_mm: Number(e.target.value) })}
          error={errorFor("parcels.0.width_mm")}
        />
        <Field
          label="Height (mm)"
          type="number"
          value={parcel.height_mm}
          onChange={(e) => setParcel({ ...parcel, height_mm: Number(e.target.value) })}
          error={errorFor("parcels.0.height_mm")}
        />
        <Field
          label="Weight (g)"
          type="number"
          value={parcel.weight_g}
          onChange={(e) => setParcel({ ...parcel, weight_g: Number(e.target.value) })}
          error={errorFor("parcels.0.weight_g")}
        />
        <Field
          label="Declared value (R)"
          type="number"
          value={declared}
          onChange={(e) => setDeclared(e.target.value)}
          placeholder="0"
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button loading={test.isPending} onClick={() => test.mutate()}>
          Get live rates
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setCollection(SAMPLE.collection);
            setDelivery(SAMPLE.delivery);
          }}
        >
          Reset to sample route
        </Button>
        {result?.elapsed_ms !== undefined && (
          <span className="text-sm text-ink-500">
            {result.elapsed_ms} ms
            {result.account_id ? `, account ${result.account_id}` : ""}
          </span>
        )}
      </div>

      {test.isPending && (
        <p className="mt-4 flex items-center gap-2 text-sm text-ink-500">
          <Spinner /> Asking the carrier
        </p>
      )}

      {result && !result.ok && (
        <div className="mt-4">
          <Alert tone={result.stage === "validation" ? "warn" : "bad"}>
            <p className="font-medium">{result.detail}</p>
            {result.stage === "validation" && (
              <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm">
                {result.validation
                  .filter((p) => p.severity === "error")
                  .map((problem) => (
                    <li key={problem.field}>
                      <span className="font-mono text-xs">{problem.field}</span>: {problem.message}
                    </li>
                  ))}
              </ul>
            )}
          </Alert>
        </div>
      )}

      {/* Warnings survive a successful quote: the parcel shipped, and there is
          still something worth checking before it goes out for real. */}
      {warnings.length > 0 && (
        <div className="mt-4">
          <Alert tone="attention">
            <ul className="list-disc space-y-0.5 pl-5 text-sm">
              {warnings.map((problem) => (
                <li key={problem.field + problem.message}>{problem.message}</li>
              ))}
            </ul>
          </Alert>
        </div>
      )}

      {result?.ok && result.rates && (
        <div className="mt-5">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-bold tracking-tight">{result.detail}</h3>
            <p className="text-sm text-ink-500">Priced on the {result.rate_card} rate card</p>
          </div>
          <Table head={["Service", "Code", "Carrier cost", "Customer pays", "Margin", "Transit", "Cut-off"]}>
            {result.rates.map((rate) => (
              <tr key={rate.service + rate.carrier_code}>
                <td className="ws-td font-medium">{rate.service}</td>
                <td className="ws-td font-mono text-xs text-ink-500">{rate.carrier_code || "-"}</td>
                <td className="ws-td text-right tabular-nums text-ink-600">{money(rate.cost_cents)}</td>
                <td className="ws-td text-right font-semibold tabular-nums">{money(rate.price_cents)}</td>
                <td className="ws-td text-right tabular-nums">
                  <span className={cx(rate.margin_cents <= 0 ? "text-signal-bad" : "text-signal-good")}>
                    {money(rate.margin_cents)}
                  </span>
                  <span className="ml-1 text-xs text-ink-400">{rate.margin_percent}%</span>
                </td>
                <td className="ws-td text-ink-600">{transitCopy(rate.transit_days)}</td>
                <td className="ws-td text-ink-500">{rate.collection_cutoff || "-"}</td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </Card>
  );
}

function AddressFields({
  title,
  prefix,
  value,
  onChange,
  errorFor,
  warningFor,
}: {
  title: string;
  prefix: string;
  value: Address;
  onChange: (next: Address) => void;
  errorFor: (field: string) => string | undefined;
  warningFor: (field: string) => string | undefined;
}) {
  const set = (key: keyof Address) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange({ ...value, [key]: event.target.value });
  const field = (name: string) => `${prefix}.${name}`;

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="font-bold tracking-tight">{title}</h3>
        {warningFor(field("province")) && <Badge tone="attention">check province</Badge>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Street" value={value.street} onChange={set("street")} error={errorFor(field("street"))} className="sm:col-span-2" />
        <Field label="Suburb" value={value.suburb} onChange={set("suburb")} error={errorFor(field("suburb"))} hint={warningFor(field("suburb"))} />
        <Field label="City" value={value.city} onChange={set("city")} error={errorFor(field("city"))} />
        <Select
          label="Province"
          value={value.province}
          onChange={set("province")}
          error={errorFor(field("province"))}
          options={PROVINCES.map((p) => ({ value: p, label: p }))}
        />
        <Field
          label="Postal code"
          inputMode="numeric"
          maxLength={4}
          value={value.postal_code}
          onChange={set("postal_code")}
          error={errorFor(field("postal_code"))}
          hint={warningFor(field("province"))}
        />
        <Select
          label="Address type"
          value={value.type ?? "business"}
          onChange={set("type")}
          error={errorFor(field("type"))}
          options={[
            { value: "business", label: "Business" },
            { value: "residential", label: "Residential" },
            { value: "counter", label: "Counter" },
            { value: "locker", label: "Locker" },
          ]}
        />
        <Field label="Contact name" value={value.contact_name} onChange={set("contact_name")} error={errorFor(field("contact_name"))} />
        <Field label="Phone" value={value.phone} onChange={set("phone")} error={errorFor(field("phone"))} hint={warningFor(field("phone"))} />
        <Field label="Email" type="email" value={value.email ?? ""} onChange={set("email")} error={errorFor(field("email"))} />
      </div>
    </div>
  );
}
